import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, Select, Switch, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { formatFullTime } from '../../../utils/timeFormat';
import { MANAGED_FULFILLMENT_STATUS_LABEL } from '../orders/orderManageData';
import { REGION_OPTIONS } from '../packages/packageFormOptions';
import { GiftBenefitModal } from './GiftBenefitModal';
import {
  getEntitlementPackageDetail,
  getEntitlementPackages,
  publishEntitlementPackage,
  queryEntitlementGiftOrders,
  type EntitlementGiftRecord,
  type EntitlementGiftRecordQuery,
  type EntitlementPackageListItem,
  type EntitlementPriceSummary,
  type EntitlementSpecification
} from './benefitManageData';

type TabKey = 'list' | 'gifts';

type GiftFilterValues = {
  adminName?: string;
  userName?: string;
  packageCode?: string;
  giftTimeRange?: [Dayjs, Dayjs];
};

const CODE_21_PATTERN = /^[0-9]{21}$/;

function normalizeCodeFilter(value?: string) {
  const text = value?.trim() ?? '';
  return text || undefined;
}

function toGiftTimeBound(day: Dayjs, endOfDay: boolean) {
  const date = day.format('YYYY-MM-DD');
  return endOfDay ? `${date}T23:59:59+08:00` : `${date}T00:00:00+08:00`;
}

function fulfillmentTone(status?: string) {
  switch (status?.trim()) {
    case 'FULFILLED':
      return 'success';
    case 'PENDING':
      return 'pending';
    case 'MANUAL_REVIEW':
      return 'review';
    default:
      return '';
  }
}

function fulfillmentLabel(status?: string) {
  const key = status?.trim() ?? '';
  if (!key) return '—';
  return MANAGED_FULFILLMENT_STATUS_LABEL[key] || key;
}

function formatMinorAmount(amountMinor?: number, fractionDigits?: number) {
  if (amountMinor == null || !Number.isFinite(amountMinor)) return '—';
  const minor = Math.trunc(amountMinor);
  if (!Number.isSafeInteger(minor)) return String(amountMinor);
  const digits = fractionDigits == null || fractionDigits < 0 ? 0 : Math.min(Math.trunc(fractionDigits), 8);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digits === 0) return sign + abs.toLocaleString('en-US');
  const factor = 10 ** digits;
  const whole = Math.trunc(abs / factor);
  const fraction = String(abs % factor).padStart(digits, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${fraction}`;
}

function regionText(regionCode?: string) {
  if (!regionCode) return '—';
  const label = REGION_OPTIONS.find((item) => item.value === regionCode)?.label;
  return label ? `${label} ${regionCode}` : regionCode;
}

function priceText(item: EntitlementPriceSummary) {
  return `${regionText(item.regionCode)} ${formatMinorAmount(item.amountMinor, item.currencyFractionDigits)} ${item.currency?.trim() || ''}`.trim();
}

function periodLabel(validityDays?: number | null) {
  if (validityDays == null) return '永久';
  if (Number.isInteger(validityDays) && validityDays >= 1) return `${validityDays} 天`;
  return '—';
}

function SpecBlocks({
  specs,
  quotaUnit
}: {
  specs?: EntitlementSpecification[];
  quotaUnit?: string;
}) {
  if (!specs?.length) return <span className='admin-cell-secondary'>暂无规格</span>;
  const unit = quotaUnit?.trim() || '次';
  return (
    <div className='admin-pkg-price-list'>
      {specs.map((spec, index) => {
        const prices = (spec.prices ?? []).map(priceText).filter(Boolean);
        const quota =
          typeof spec.quotaLimit === 'number' && Number.isFinite(spec.quotaLimit)
            ? `${spec.quotaLimit}${unit}`
            : `—${unit}`;
        return (
          <div
            key={`${spec.quotaLimit ?? ''}-${spec.validityDays ?? 'forever'}-${index}`}
            className='admin-pkg-price-block'
          >
            <div className='admin-pkg-price-main'>
              {quota}
              <span className='admin-benefit-spec-sep'>·</span>
              {periodLabel(spec.validityDays)}
            </div>
            <div className='admin-pkg-price-sub'>{prices.length ? prices.join('；') : '暂无价格'}</div>
          </div>
        );
      })}
    </div>
  );
}

export function BenefitManage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('list');
  const [packages, setPackages] = useState<EntitlementPackageListItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [giftForm] = Form.useForm<GiftFilterValues>();
  const [giftFilters, setGiftFilters] = useState<GiftFilterValues>({});
  const [gifts, setGifts] = useState<EntitlementGiftRecord[]>([]);
  const [giftPageNum, setGiftPageNum] = useState(1);
  const [giftPageSize, setGiftPageSize] = useState(10);
  const [giftTotal, setGiftTotal] = useState(0);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftReloadKey, setGiftReloadKey] = useState(0);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftBenefitId, setGiftBenefitId] = useState<string | undefined>();
  const [packageOptions, setPackageOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [packageOptionsLoading, setPackageOptionsLoading] = useState(false);
  const [publishingCodes, setPublishingCodes] = useState<string[]>([]);
  const publishingRef = useRef(new Set<string>());

  useEffect(() => {
    if (tab !== 'gifts') return;
    let cancelled = false;
    setPackageOptionsLoading(true);
    const loadPackages = async () => {
      const collected: Array<{ value: string; label: string }> = [];
      for (let page = 1; page <= 10; page += 1) {
        const res = await getEntitlementPackages({ pageNum: page, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权益包选项加载失败');
          break;
        }
        records.forEach((item) => {
          const packageCode = item.packageCode?.trim() ?? '';
          if (!CODE_21_PATTERN.test(packageCode)) return;
          collected.push({
            value: packageCode,
            label: item.displayName?.trim() || packageCode
          });
        });
        if (records.length < 100) break;
      }
      if (!cancelled) setPackageOptions(collected);
    };
    loadPackages()
      .catch(() => {
        if (!cancelled) message.error('权益包选项加载失败');
      })
      .finally(() => {
        if (!cancelled) setPackageOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  useEffect(() => {
    if (tab !== 'gifts') return;
    let cancelled = false;
    setGiftLoading(true);
    const range = giftFilters.giftTimeRange;
    const query: EntitlementGiftRecordQuery = {
      pageNum: giftPageNum,
      pageSize: giftPageSize,
      adminName: normalizeCodeFilter(giftFilters.adminName),
      userName: normalizeCodeFilter(giftFilters.userName),
      packageCode: normalizeCodeFilter(giftFilters.packageCode),
      startTime: range?.[0] ? toGiftTimeBound(range[0], false) : undefined,
      endTime: range?.[1] ? toGiftTimeBound(range[1], true) : undefined
    };
    queryEntitlementGiftOrders(query)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '赠送记录加载失败');
          setGifts([]);
          setGiftTotal(0);
          return;
        }
        setGifts(res.records);
        setGiftTotal(res.total);
      })
      .catch(() => {
        if (!cancelled) message.error('赠送记录加载失败');
      })
      .finally(() => {
        if (!cancelled) setGiftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, giftPageNum, giftPageSize, giftFilters, giftReloadKey]);

  useEffect(() => {
    if (tab !== 'list') return;
    let cancelled = false;
    setLoading(true);
    getEntitlementPackages({ pageNum, pageSize })
      .then((res) => {
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权益包列表加载失败');
          setPackages([]);
          setTotal(0);
          return;
        }
        const pageTotal = res.content?.total;
        setPackages(records);
        setTotal(typeof pageTotal === 'number' ? pageTotal : records.length);
      })
      .catch(() => {
        if (!cancelled) message.error('权益包列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, pageNum, pageSize]);

  const openGiftModal = (packageCode?: string) => {
    setGiftBenefitId(packageCode?.trim() || undefined);
    setGiftOpen(true);
  };

  const searchGiftRecords = (values: GiftFilterValues) => {
    const packageCode = normalizeCodeFilter(values.packageCode);
    if (packageCode && !CODE_21_PATTERN.test(packageCode)) {
      message.warning('权益包编码须为 21 位数字');
      return;
    }
    setGiftPageNum(1);
    setGiftFilters({
      adminName: normalizeCodeFilter(values.adminName),
      userName: normalizeCodeFilter(values.userName),
      packageCode,
      giftTimeRange: values.giftTimeRange
    });
  };

  const resetGiftFilters = () => {
    giftForm.resetFields();
    setGiftPageNum(1);
    setGiftFilters({});
  };

  const publishPackage = async (row: EntitlementPackageListItem, checked: boolean) => {
    const packageCode = row.packageCode?.trim() ?? '';
    if (!packageCode) {
      message.warning('缺少权益包编码');
      return;
    }
    if (!checked) {
      message.warning('下架接口尚未接入');
      return;
    }
    if (row.status === 'PUBLISHED' || publishingRef.current.has(packageCode)) return;
    publishingRef.current.add(packageCode);
    setPublishingCodes((prev) => [...prev, packageCode]);
    try {
      const detailRes = await getEntitlementPackageDetail(packageCode);
      if (detailRes.code !== 200 || !detailRes.content?.data) {
        if (detailRes.code !== -1) message.error(detailRes.message || '权益包详情加载失败');
        return;
      }
      const hash = detailRes.content.configHash;
      if (typeof hash !== 'string' || !hash) {
        message.warning('缺少配置校验值，请稍后重试');
        return;
      }
      const res = await publishEntitlementPackage(packageCode, hash);
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '上架失败');
        return;
      }
      const nextStatus = res.content?.data?.status === 'PUBLISHED' || res.content?.data?.status === 'DISABLED'
        ? res.content.data.status
        : 'PUBLISHED';
      setPackages((prev) =>
        prev.map((item) => (item.packageCode === row.packageCode ? { ...item, status: nextStatus } : item))
      );
      message.success(res.message || '已上架');
    } catch {
      message.error('上架失败');
    } finally {
      publishingRef.current.delete(packageCode);
      setPublishingCodes((prev) => prev.filter((code) => code !== packageCode));
    }
  };

  const confirmDelete = (row: EntitlementPackageListItem) => {
    const name = row.displayName?.trim() || row.packageCode || '该权益包';
    message.warning(`「${name}」暂未接入删除接口`);
  };

  const handleGiftSuccess = () => {
    setTab('gifts');
    setGiftPageNum(1);
    setGiftReloadKey((key) => key + 1);
  };

  const listColumns: ColumnsType<EntitlementPackageListItem> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1
    },
    {
      title: '权益包编码',
      dataIndex: 'packageCode',
      width: 200,
      render: (code?: string) => <span className='admin-pkg-code-id'>{code || '—'}</span>
    },
    {
      title: '权益包名称',
      dataIndex: 'displayName',
      width: 160,
      render: (name?: string) => <span className='admin-cell-primary'>{name || '—'}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 88,
      align: 'center',
      render: (status: string | undefined, row) => {
        const packageCode = row.packageCode?.trim() ?? '';
        return (
          <Switch
            size='small'
            checked={status === 'PUBLISHED'}
            loading={publishingCodes.includes(packageCode)}
            disabled={(status !== 'PUBLISHED' && status !== 'DISABLED') || publishingCodes.includes(packageCode)}
            onChange={(checked) => publishPackage(row, checked)}
          />
        );
      }
    },
    {
      title: '规格',
      dataIndex: 'specifications',
      width: 420,
      render: (specs: EntitlementSpecification[] | undefined, row) => (
        <SpecBlocks specs={specs} quotaUnit={row.quotaUnit} />
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button type='button' className='admin-link-btn' onClick={() => openGiftModal(row.packageCode)}>
            赠送
          </button>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => {
              if (!row.packageCode) {
                message.warning('缺少权益包编码');
                return;
              }
              navigate(`/devops/creator-outreach/benefits/new?id=${row.packageCode}`);
            }}
          >
            编辑
          </button>
          <button
            type='button'
            className='admin-link-btn admin-link-btn--danger'
            onClick={() => confirmDelete(row)}
          >
            删除
          </button>
        </div>
      )
    }
  ];

  const giftColumns: ColumnsType<EntitlementGiftRecord> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (giftPageNum - 1) * giftPageSize + index + 1
    },
    {
      title: '订单号',
      dataIndex: 'orderNo',
      width: 200,
      render: (value?: string) => <span className='admin-pkg-code-id'>{value?.trim() || '—'}</span>
    },
    {
      title: '赠送时间',
      dataIndex: 'giftedAt',
      width: 180,
      render: (value?: string) => formatFullTime(value) || '—'
    },
    {
      title: '赠送人',
      key: 'admin',
      width: 180,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.adminName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.adminCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '接收人',
      key: 'user',
      width: 180,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.userName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.userCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '权益包',
      key: 'package',
      width: 200,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.packageName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.packageCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '额度',
      key: 'quota',
      width: 100,
      render: (_, row) => {
        if (row.quotaLimit == null) return '—';
        return `${row.quotaLimit}${row.quotaUnit?.trim() || ''}`;
      }
    },
    {
      title: '有效期',
      dataIndex: 'validityDays',
      width: 90,
      render: (value?: number | null) => periodLabel(value)
    },
    {
      title: '发放状态',
      dataIndex: 'fulfillmentStatus',
      width: 120,
      render: (value?: string) => {
        const tone = fulfillmentTone(value);
        return (
          <span className={`admin-order-status-pill${tone ? ` admin-order-status-pill--${tone}` : ''}`}>
            <i className='admin-order-status-dot' aria-hidden />
            {fulfillmentLabel(value)}
          </span>
        );
      }
    },
    {
      title: '赠送原因',
      dataIndex: 'reason',
      width: 200,
      ellipsis: true,
      render: (value?: string) => value?.trim() || '—'
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='admin-pkg-toolbar'>
        <div className='admin-pkg-tabs' role='tablist'>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'list'}
            className={`admin-pkg-tab${tab === 'list' ? ' is-active' : ''}`}
            onClick={() => setTab('list')}
          >
            单独权益列表
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'gifts'}
            className={`admin-pkg-tab${tab === 'gifts' ? ' is-active' : ''}`}
            onClick={() => setTab('gifts')}
          >
            赠送记录
          </button>
        </div>
      </div>

      {tab === 'list' ? (
        <>
          <div className='admin-filter-bar admin-pkg-filter-bar'>
            <Button
              type='primary'
              className='admin-pkg-create-btn admin-pkg-add-btn'
              onClick={() => navigate('/devops/creator-outreach/benefits/new')}
            >
              新增
            </Button>
          </div>

          <Table<EntitlementPackageListItem>
            rowKey={(row, index) => row.packageCode || `${row.displayName ?? ''}-${index}`}
            columns={listColumns}
            dataSource={packages}
            loading={loading}
            pagination={{
              current: pageNum,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (count) => `共 ${count} 条`,
              onChange: (current, size) => {
                const nextSize = Math.min(100, Math.max(1, size));
                if (nextSize !== pageSize) {
                  setPageNum(1);
                  setPageSize(nextSize);
                  return;
                }
                setPageNum(current);
              }
            }}
            scroll={{ x: 1100 }}
            className='admin-user-table'
            size='middle'
          />
        </>
      ) : (
        <>
          <Form
            form={giftForm}
            layout='horizontal'
            labelAlign='right'
            colon
            className='admin-order-filter'
            onFinish={searchGiftRecords}
          >
            <div className='admin-order-filter-grid'>
              <Form.Item name='adminName' label='赠送人'>
                <Input allowClear placeholder='请输入赠送人名称' maxLength={128} />
              </Form.Item>
              <Form.Item name='userName' label='接收人'>
                <Input allowClear placeholder='请输入接收人名称' maxLength={128} />
              </Form.Item>
              <Form.Item name='packageCode' label='权益包'>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  placeholder='全部'
                  loading={packageOptionsLoading}
                  options={packageOptions}
                />
              </Form.Item>
              <Form.Item name='giftTimeRange' label='赠送时间'>
                <DatePicker.RangePicker className='w-full' allowEmpty={[true, true]} />
              </Form.Item>
            </div>
            <div className='admin-order-filter-actions'>
              <Button onClick={resetGiftFilters}>重置</Button>
              <Button type='primary' htmlType='submit' className='admin-pkg-query-btn'>
                查询
              </Button>
              <Button type='primary' className='admin-pkg-create-btn' onClick={() => openGiftModal()}>
                赠送
              </Button>
            </div>
          </Form>
          <Table<EntitlementGiftRecord>
            rowKey={(row, index) =>
              row.orderNo || `${row.userCode ?? ''}-${row.packageCode ?? ''}-${index}`
            }
            columns={giftColumns}
            dataSource={gifts}
            loading={giftLoading}
            className='admin-user-table'
            size='middle'
            scroll={{ x: 1500 }}
            pagination={{
              current: giftPageNum,
              pageSize: giftPageSize,
              total: giftTotal,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (count) => {
                const pages = Math.max(1, Math.ceil(count / giftPageSize));
                return `共${pages}页 / ${count}条`;
              },
              onChange: (current, size) => {
                const nextSize = Math.min(100, Math.max(1, size));
                if (nextSize !== giftPageSize) {
                  setGiftPageNum(1);
                  setGiftPageSize(nextSize);
                  return;
                }
                setGiftPageNum(current);
              }
            }}
          />
        </>
      )}

      <GiftBenefitModal
        open={giftOpen}
        initialPackageCode={giftBenefitId}
        onClose={() => {
          setGiftOpen(false);
          setGiftBenefitId(undefined);
        }}
        onSuccess={handleGiftSuccess}
      />
    </div>
  );
}
